import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Copy, Upload, PencilIcon, X } from 'lucide-react';
import Modal from '@/components/common/Modal/Modal';
import { storage, getUID, encode, decode, C } from '@/utils';
import { getSocket } from '@/utils/socketInstance';
import { useToast } from '@/hooks/useToast';
import { useKYCStatus } from '@/hooks/useSettings';
import { useUserInfo, useBetWinCount, useWagerCheck } from '@/hooks/useProfile';
import styles from './ProfileModal.module.css';

/**
 * ProfileModal Component
 * User profile modal with statistics, rank, and account management
 * Opens via route: /modal?mode=profile
 * Full screen on mobile, modal on desktop
 */
const ProfileModal = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode');
  const { showError, showSuccess } = useToast();

  // Get user info from storage (must be before state that uses it)
  const currentName = storage.getKey('name') || localStorage.getItem('name') || 'Guest';
  const displayUid = getUID();
  const DEFAULT_AVATAR_PATH = '/assets/images/profile-jk.webp';

  const [isOpen, setIsOpen] = useState(false);
  const [showEditUi, setShowEditUi] = useState(false);
  const [newUsername, setNewUsername] = useState(currentName);
  const [isUpdatingUsername, setIsUpdatingUsername] = useState(false);
  const [updateError, setUpdateError] = useState(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  
  const fileInputRef = useRef(null);

  // Fetch KYC status
  const { data: kycStatusData } = useKYCStatus();
  const kycStatus = kycStatusData?.status || 'Unverified';

  // Fetch user info using React Query
  const { 
    data: userData, 
    isLoading: isLoadingUserInfo,
    refetch: refetchUserInfo 
  } = useUserInfo(isOpen);

  // Fetch bet statistics using React Query
  const { 
    data: statsData, 
    isLoading: isLoadingStats 
  } = useBetWinCount(isOpen);

  // Fetch wager check data using React Query
  const { 
    data: wagerData, 
    isLoading: isLoadingWager 
  } = useWagerCheck(isOpen);

  const stats = statsData || { betCount: 0, winCount: 0 };
  const isLoading = isLoadingUserInfo || isLoadingStats || isLoadingWager;

  // Handle route-based modal opening
  useEffect(() => {
    const pathname = location.pathname;
    const shouldOpen = pathname === '/modal' && mode === 'profile';
    setIsOpen(shouldOpen);
  }, [location.pathname, mode]);

  const handleClose = () => {
    setIsOpen(false);
    setShowEditUi(false);
    // Navigate back or to home when modal closes
    if (location.pathname !== '/') {
      navigate('/');
    } else {
      navigate(-1);
    }
  };

  const handleCopyUID = () => {
    navigator.clipboard.writeText(displayUid);
    showSuccess('User ID copied to clipboard');
  };

  const handleVerificationClick = () => {
    if (kycStatus !== 'Verified') {
      // Close profile modal and navigate to full page settings with verification tab
      handleClose();
      navigate('/settings?section=verification');
    }
  };

  const handleUsernameChange = (event) => {
    setNewUsername(event.target.value);
    setUpdateError(null);
  };

  const updateUsername = async () => {
    if (!newUsername.trim()) {
      setUpdateError('Username cannot be empty');
      return;
    }

    setIsUpdatingUsername(true);
    setUpdateError(null);

    try {
      const response = await fetch('https://api.jackopot.com/editProfile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          uid: displayUid,
          username: newUsername.trim()
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update username');
      }

      storage.setKey('name', newUsername);
      localStorage.setItem('name', newUsername);
      setShowEditUi(false);
      showSuccess('Username updated successfully');

      // Refetch user info using React Query
      refetchUserInfo();
    } catch (error) {
      setUpdateError('Failed to update username. Please try again.');
      showError('Failed to update username');
    } finally {
      setIsUpdatingUsername(false);
    }
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      showError('Image too large. Maximum size is 2MB.');
      return;
    }

    if (!file.type.match('image.*')) {
      showError('Please select an image file.');
      return;
    }

    try {
      setIsUploadingImage(true);
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result;
        // Here you would upload to your API
        // For now, just show success
        showSuccess('Avatar updated successfully!');
        setIsUploadingImage(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error updating avatar:', error);
      showError('Failed to update avatar. Please try again.');
      setIsUploadingImage(false);
    }
  };

  // Calculate statistics
  const wagered = userData?.profit || 0;
  const totalBets = stats.betCount || 0;
  const averageWager = totalBets > 0 ? (wagered / totalBets).toFixed(2) : '0.00';
  const totalTipped = 0; // Placeholder - implement if needed

  // Calculate wager progress from API data
  const wagerInfo = wagerData || {};
  const currentWager = wagerInfo.wager || 0;
  const targetWager = wagerInfo.target || 0;
  const totalDeposit = wagerInfo.total_deposit || 0;
  const remainingWager = Math.max(0, targetWager - currentWager);
  const progressPercentage = targetWager > 0 
    ? Math.min(100, (currentWager / targetWager) * 100) 
    : 0;

  // Get joined date (placeholder - implement if you have this data)
  const joinedDate = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: '2-digit', 
    day: '2-digit' 
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="md"
      title="Player Profile"
      showCloseButton={true}
      className={styles.modal}
    >
      <div className={styles.modalBody}>
        {isLoading ? (
          <div className={styles.loadingContainer}>
            <div className={styles.loader}>
              <span className={styles.loaderText}>loading</span>
              <span className={styles.load}></span>
            </div>
          </div>
        ) : showEditUi ? (
          <div className={styles.editContainer}>
            <div className={styles.avatarSection}>
              <div className={styles.avatarContainerEdit}>
                <img
                  src={DEFAULT_AVATAR_PATH}
                  alt="Profile"
                  className={styles.avatarEdit}
                  onError={(e) => {
                    e.target.src = DEFAULT_AVATAR_PATH;
                  }}
                />
                <button
                  className={styles.avatarEditButton}
                  onClick={handleImageClick}
                  disabled={isUploadingImage}
                >
                  {isUploadingImage ? (
                    <div className={styles.spinner}></div>
                  ) : (
                    <Upload size={14} />
                  )}
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageChange}
                  accept="image/*"
                  style={{ display: 'none' }}
                />
              </div>
            </div>

            <div className={styles.inputGroup}>
              <label className={styles.label}>Username</label>
              <input
                type="text"
                className={styles.input}
                value={newUsername || currentName}
                onChange={handleUsernameChange}
                placeholder="Enter new username"
                disabled={isUpdatingUsername}
              />
              {updateError && (
                <p className={styles.errorText}>{updateError}</p>
              )}
              <p className={styles.helpText}>
                Do not use special punctuation, otherwise your account may not be supported.
              </p>
            </div>

            <button
              className={styles.saveButton}
              onClick={updateUsername}
              disabled={isUpdatingUsername}
            >
              {isUpdatingUsername ? 'Updating...' : 'Save Changes'}
            </button>

            <button
              className={styles.cancelButton}
              onClick={() => {
                setShowEditUi(false);
                setNewUsername('');
                setUpdateError(null);
              }}
            >
              Cancel
            </button>
          </div>
        ) : (
          <>
            {/* User Identity Section */}
            <div className={styles.userSection}>
              <div className={styles.avatarContainer}>
                <img
                  src={DEFAULT_AVATAR_PATH}
                  alt="Profile"
                  className={styles.avatar}
                  onError={(e) => {
                    e.target.src = DEFAULT_AVATAR_PATH;
                  }}
                />
              </div>
              <div className={styles.userInfo}>
                <div className={styles.usernameRow}>
                  <h3 className={styles.username}>{currentName}</h3>
                  {kycStatus === 'Verified' ? (
                    <span
                      className={styles.verifiedBadge}
                      title="Verified Account"
                    >
                      <img 
                        src="/assets/images/verified-account.webp" 
                        alt="Verified" 
                        className={styles.verifiedIcon}
                      />
                      <span className={styles.verifiedText}>Verified</span>
                    </span>
                  ) : (
                    <span
                      className={styles.unverifiedBadge}
                      onClick={handleVerificationClick}
                      title="Click to verify your account"
                    >
                      <img 
                        src="/assets/images/unverified-account.webp" 
                        alt="Unverified" 
                        className={styles.verifiedIcon}
                      />
                      <span className={styles.unverifiedText}>Unverified</span>
                    </span>
                  )}
                  <button
                    className={styles.copyButton}
                    onClick={handleCopyUID}
                    aria-label="Copy User ID"
                  >
                    <Copy size={16} />
                  </button>
                  <button
                    className={styles.editButton}
                    onClick={() => {
                      setNewUsername(currentName);
                      setShowEditUi(true);
                    }}
                    aria-label="Edit Profile"
                  >
                    <PencilIcon size={16} />
                  </button>
                </div>
                <p className={styles.joinedDate}>Joined Date: {joinedDate}</p>
              </div>
            </div>

            {/* Turnover Section */}
            <div className={styles.rankSection}>
              <h4 className={styles.sectionTitle}>Turnover</h4>
              <div className={styles.rankCards}>
                <div className={styles.rankCard}>
                  <p className={styles.rankLabel}>Total Wager</p>
                  <p className={styles.rankValue}>
                    ₹{parseFloat(currentWager).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
                <div className={styles.rankCard}>
                  <p className={styles.rankLabel}>Required Wager</p>
                  <p className={styles.rankValue}>
                    ₹{parseFloat(targetWager).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
                <div className={styles.rankCard}>
                  <p className={styles.rankLabel}>Total Deposit</p>
                  <p className={styles.rankValue}>
                    ₹{parseFloat(totalDeposit).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
                <div className={styles.rankCard}>
                  <p className={styles.rankLabel}>Progress</p>
                  <p className={styles.rankRemaining}>
                    Remaining ₹{parseFloat(remainingWager).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                  <div className={styles.progressBar}>
                    <div
                      className={styles.progressFill}
                      style={{ width: `${progressPercentage}%` }}
                    ></div>
                  </div>
                  <p className={styles.progressText}>
                    {progressPercentage.toFixed(2)}% Complete
                  </p>
                </div>
              </div>
            </div>

            {/* Account Statistics Section */}
            <div className={styles.statsSection}>
              <h4 className={styles.sectionTitle}>Account Statistics</h4>
              <div className={styles.statsGrid}>
                <div className={styles.statCard}>
                  <p className={styles.statLabel}>Wagered</p>
                  <p className={styles.statValue}>
                    ₹{parseFloat(wagered).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
                <div className={styles.statCard}>
                  <p className={styles.statLabel}>Total Bets</p>
                  <p className={styles.statValue}>{totalBets}</p>
                </div>
                <div className={styles.statCard}>
                  <p className={styles.statLabel}>Average Wager</p>
                  <p className={styles.statValue}>
                    ₹{parseFloat(averageWager).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
                <div className={styles.statCard}>
                  <p className={styles.statLabel}>Total Tipped</p>
                  <p className={styles.statValue}>
                    ₹{parseFloat(totalTipped).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
              </div>
            </div>

            {/* Close Account Section */}
            {/* <div className={styles.closeAccountSection}>
              <p className={styles.warningText}>
                You may close your account by clicking the 'Close My Account' button below. 
                All of your data will be permanently wiped and cannot be restored in the future 
                (including any VIP perks).
              </p>
              <button className={styles.closeAccountButton}>
                Close My Account
              </button>
            </div> */}
          </>
        )}
      </div>
    </Modal>
  );
};

export default ProfileModal;
